/**
 * Web Bluetooth driver for Acaia scales: the Lunar (before 2021 and 2021), Pearl, Pearl S, Pyxis and relatives.
 * EXPERIMENTAL: written from other projects' reverse-engineered notes, never tried with Taretto on real hardware.
 * Wire format lives in acaiaProtocol.ts; see docs/acaia-protocol.md.
 *
 * Lifecycle: the registry (registry.ts) creates an AcaiaScale for a device the picker or Chrome's getDevices()
 * returned. connect() opens GATT and picks the GATT layout from the services the device exposes: the classic one
 * (0x1820 with one characteristic, 0x2A80) or Microchip's UART service (separate notify and write characteristics).
 * It subscribes, and 150 ms later sends identify and the notification request, which start the weight stream. Later
 * calls (the store's auto-reconnect, "Reconnect") reuse the same BluetoothDevice and only re-open GATT.
 *
 * Frames may arrive split across notifications, or several in one: an AcaiaFrameBuffer reassembles them, and frames
 * failing their checksum are dropped (until the user accepts unverified packets after a checksum warning).
 *
 * Writes go through one queue, at least MIN_WRITE_GAP_MS apart.
 *
 * Session timers:
 * - Heartbeat: every HEARTBEAT_INTERVAL_MS, or the scale stops streaming. UART scales get identify before each one.
 * - Liveness watchdog: no weight for HANDSHAKE_RETRY_MS repeats the handshake; for STALE_MS reports onStale(true); a
 *   much longer silence while the page is visible drops the link so the store's reconnect path runs. The page
 *   becoming visible again also repeats the handshake (the heartbeat may have paused in the background).
 *
 * Units: while the scale's settings say ounces, no weight is emitted and onWarning asks for grams (no source says
 * what the weight field carries in ounces).
 *
 * Optional capabilities (types.ts): diagnostics (the packet log summary and the checksum escape hatch) and tareCheck
 * (the Lunar 2021 ignores commands on old firmware). No powerOff (no source documents one) and no keepAwake.
 */
import { connectTimeout, describeBleError, findDriverService, pickWriteMode, ScaleServiceMissingError } from './bluetooth';
import {
  ACAIA_CLASSIC_SERVICE_UUID,
  ACAIA_NAME_PREFIXES,
  ACAIA_PRIMARY_SERVICES,
  AcaiaFrameBuffer,
  acaiaCommands,
  acaiaLayoutForService,
  isAcaiaName,
  parseAcaiaFrame,
  type AcaiaLayout,
  type AcaiaRawFrame,
  type AcaiaUnits,
} from './acaiaProtocol';
import type {
  BluetoothScaleDriverDescriptor,
  RawPacketListener,
  RawWriteListener,
  ScaleConnectOptions,
  ScaleDiagnosticsCapability,
  ScaleDiagnosticsItem,
  ScaleDriver,
  ScaleReading,
  ScaleWarning,
  TareCheckCapability,
} from './types';

const CONNECT_TIMEOUT_MS = 15_000;
const CONNECT_RETRY_DELAY_MS = 600;
const WRITE_RETRY_DELAY_MS = 150;
/** aioacaia writes 100 ms apart; Beanconqueror waits 100 ms between identify and the notification request. */
export const MIN_WRITE_GAP_MS = 100;
/** After subscribing, before identify (Beanconqueror waits 150 ms, aioacaia 100 ms, de1app 500 ms). */
export const HANDSHAKE_DELAY_MS = 150;
/** The official app's cadence for the Pyxis (per pyacaia) and the shortest any source uses; known-good ones go up to 5 s. */
export const HEARTBEAT_INTERVAL_MS = 1_000;
/** No weight for this long: identify and request notifications again (de1app retries, Beanconqueror revives). */
export const HANDSHAKE_RETRY_MS = 2_000;

const WATCHDOG_CHECK_MS = 500;
/** The scale streams at about 10 Hz: no weight for this long means the last one is no longer live. */
export const STALE_MS = 1_500;
/** Silence, counted only while the page is visible, after which the link is dropped to force a reconnect. */
export const STALE_DISCONNECT_MS = 12_000;
/** Consecutive frames failing their checksum, with no good one between, before the driver warns (~2 s at 10 Hz). */
export const CHECKSUM_FAILURE_THRESHOLD = 20;

export const ACAIA_CHECKSUM_WARNING = 'Scale connected, but every packet fails its checksum (firmware mismatch), so no weight can be shown.';
export const ACAIA_UNITS_WARNING =
  'The scale is set to ounces. Taretto can’t read an Acaia’s weight in ounces reliably, so it shows none: switch the scale to grams in its settings.';
export const ACAIA_TARE_HINT =
  'Some Acaia scales, the Lunar 2021 in particular, only take commands from apps on current firmware: update the scale with Acaia’s updater app, or tare on the scale itself.';

/** Registry entry (registry.ts). Filters by name, plus 0x1820 (classic scales advertise it). */
export const acaiaDriver: BluetoothScaleDriverDescriptor = {
  kind: 'acaia',
  transport: 'bluetooth',
  label: 'Acaia Lunar / Pearl / Pyxis',
  shortName: 'Acaia',
  filters: [...ACAIA_NAME_PREFIXES.map((namePrefix) => ({ namePrefix })), { services: [ACAIA_CLASSIC_SERVICE_UUID] }],
  bluefyFilter: { namePrefix: 'LUNAR' },
  optionalServices: [...ACAIA_PRIMARY_SERVICES],
  primaryServices: ACAIA_PRIMARY_SERVICES,
  matches: isAcaiaName,
  create: (device) => new AcaiaScale(device),
  capabilities: ['diagnostics', 'tareCheck'],
  experimental: true,
  copy: {
    pickerName: 'LUNAR…/PEARL…/PYXIS…/ACAIA…',
    vendorApp: 'the Acaia app',
  },
};

export type AcaiaChecksumMode = 'strict' | 'ignore';

export interface AcaiaDiagnostics {
  /** Notifications received. */
  packets: number;
  /** Frames taken out of the stream (checksum good, or accepted unverified). */
  frames: number;
  weightPackets: number;
  /** Frames whose checksums didn't match. Dropped unless checksumMode is 'ignore'. */
  badChecksum: number;
  /** Bytes outside any frame. */
  junkBytes: number;
  /** Headers with an impossible length byte. */
  badLength: number;
  /** Frames too short for their type, or a weight with an unknown decimal byte. Dropped. */
  malformed: number;
  /** Weights whose bytes 2-3 weren't zero (the 16-bit value may be off). Used. */
  highBytes: number;
  /** Weights not emitted because the scale is set to ounces. */
  ouncesDropped: number;
  /** Frames of a kind or event type Taretto doesn't use. */
  other: number;
  buttons: number;
  settings: number;
  handshakes: number;
  heartbeatsSent: number;
  layout: AcaiaLayout['kind'] | null;
  units: AcaiaUnits | null;
  unitsByte: number | null;
  batteryPct: number | null;
  checksumMode: AcaiaChecksumMode;
  writeMode: 'withResponse' | 'withoutResponse' | 'legacy' | null;
  stale: boolean;
}

interface Session {
  server: BluetoothRemoteGATTServer;
  layout: AcaiaLayout;
  writeChar: BluetoothRemoteGATTCharacteristic;
  timers: Array<ReturnType<typeof setInterval>>;
  timeouts: Array<ReturnType<typeof setTimeout>>;
  cleanup: Array<() => void>;
  buffer: AcaiaFrameBuffer;
  lastWeightAt: number;
  /** Last time the page became visible again or the watchdog timer itself was frozen. */
  resumeAt: number;
  lastCheckAt: number;
  lastHandshakeAt: number;
  stale: boolean;
  heartbeatPending: boolean;
  /** Consecutive frames failing their checksum. */
  badStreak: number;
  units: AcaiaUnits | null;
  warning: ScaleWarning | null;
}

export class AcaiaScale implements ScaleDriver {
  readonly kind = 'acaia' as const;

  private readonly device: BluetoothDevice;
  private session: Session | null = null;
  private connecting: Promise<void> | null = null;
  /** Incremented to invalidate an in-flight connection attempt (timeout / disconnect()). */
  private attempt = 0;
  private writeChain: Promise<void> = Promise.resolve();
  private lastWriteAt = Number.NEGATIVE_INFINITY;
  private checksumMode: AcaiaChecksumMode = 'strict';
  private batteryPct: number | null = null;
  private readonly readingListeners = new Set<(r: ScaleReading) => void>();
  private readonly disconnectListeners = new Set<(reason?: string) => void>();
  private readonly rawListeners = new Set<RawPacketListener>();
  private readonly writeListeners = new Set<RawWriteListener>();
  private readonly staleListeners = new Set<(stale: boolean) => void>();
  private readonly warningListeners = new Set<(warning: ScaleWarning | null) => void>();
  private diag: AcaiaDiagnostics = emptyDiagnostics('strict');

  readonly diagnostics: ScaleDiagnosticsCapability = {
    summary: () => ({ badge: this.diag.layout === 'uart' ? 'Acaia (UART)' : this.diag.layout === 'classic' ? 'Acaia (classic)' : 'Acaia', items: diagnosticsItems(this.stats) }),
    acceptUnverifiedPackets: () => this.setChecksumMode('ignore'),
  };

  readonly tareCheck: TareCheckCapability = {
    failureMessage: () => `The scale didn’t zero. ${ACAIA_TARE_HINT}`,
  };

  constructor(device: BluetoothDevice) {
    this.device = device;
  }

  get name(): string {
    return this.device.name?.trim() || 'Acaia scale';
  }

  get connected(): boolean {
    return this.session?.server.connected === true;
  }

  get stats(): AcaiaDiagnostics {
    return { ...this.diag };
  }

  /** The GATT layout of the current (or last) session. */
  get layout(): AcaiaLayout['kind'] | null {
    return this.diag.layout;
  }

  connect(options: ScaleConnectOptions = {}): Promise<void> {
    if (this.connected) return Promise.resolve();
    if (this.connecting) return this.connecting;
    this.endSession();
    const attempt = ++this.attempt;
    const p = this.establish(attempt, options.timeoutMs ?? CONNECT_TIMEOUT_MS).finally(() => {
      if (this.connecting === p) this.connecting = null;
    });
    this.connecting = p;
    return p;
  }

  /** Closes the session, or cancels (and aborts) a pending connection attempt. Safe to call repeatedly. */
  async disconnect(): Promise<void> {
    this.attempt++;
    const hadSession = this.session !== null;
    const active = hadSession || this.connecting !== null;
    this.connecting = null;
    this.endSession();
    this.device.removeEventListener('gattserverdisconnected', this.onGattDisconnected);
    if (active) this.device.gatt?.disconnect();
    if (hadSession) this.emitDisconnect();
  }

  /** Not confirmed reliably by the scale: the engine sees the tare land in the weight stream (and tareCheck). */
  tare(): Promise<void> {
    return this.send('tare', acaiaCommands.tare());
  }

  startTimer(): Promise<void> {
    return this.send('start timer', acaiaCommands.startTimer());
  }

  stopTimer(): Promise<void> {
    return this.send('stop timer', acaiaCommands.stopTimer());
  }

  resetTimer(): Promise<void> {
    return this.send('reset timer', acaiaCommands.resetTimer());
  }

  /** 'ignore' after the user accepts unverified packets: frames are used whatever their checksums say. */
  setChecksumMode(mode: AcaiaChecksumMode): void {
    this.checksumMode = mode;
    this.diag.checksumMode = mode;
    const s = this.session;
    if (mode === 'ignore' && s?.warning?.code === 'checksum') this.setWarning(s, null);
  }

  onReading(cb: (r: ScaleReading) => void): () => void {
    return subscribe(this.readingListeners, cb);
  }

  onDisconnect(cb: (reason?: string) => void): () => void {
    return subscribe(this.disconnectListeners, cb);
  }

  /** Every notification as received, before reassembly. */
  onRawPacket(cb: RawPacketListener): () => void {
    return subscribe(this.rawListeners, cb);
  }

  /** Every write attempt to the scale, with whether it went out. */
  onRawWrite(cb: RawWriteListener): () => void {
    return subscribe(this.writeListeners, cb);
  }

  onStale(cb: (stale: boolean) => void): () => void {
    return subscribe(this.staleListeners, cb);
  }

  onWarning(cb: (warning: ScaleWarning | null) => void): () => void {
    return subscribe(this.warningListeners, cb);
  }

  // ---------------------------------------------------------------- connection

  private async establish(attempt: number, timeoutMs: number): Promise<void> {
    const device = this.device;
    device.addEventListener('gattserverdisconnected', this.onGattDisconnected);
    const timeout = connectTimeout(timeoutMs);
    try {
      await Promise.race([this.openSessionWithRetry(device, attempt), timeout.promise]);
    } catch (e) {
      if (attempt === this.attempt) {
        this.attempt++;
        this.endSession();
        device.gatt?.disconnect();
      }
      throw describeBleError(e, acaiaDriver);
    } finally {
      timeout.clear();
    }
  }

  private async openSessionWithRetry(device: BluetoothDevice, attempt: number): Promise<void> {
    try {
      await this.openSession(device, attempt);
    } catch (e) {
      // Android's BLE stack regularly fails the first GATT connect "for unknown reason"; one retry fixes most.
      const name = e instanceof Error ? e.name : '';
      if (attempt !== this.attempt || e instanceof ScaleServiceMissingError || name === 'NotFoundError' || name === 'SecurityError') throw e;
      await delay(CONNECT_RETRY_DELAY_MS);
      this.assertCurrent(attempt);
      await this.openSession(device, attempt);
    }
  }

  private async openSession(device: BluetoothDevice, attempt: number): Promise<void> {
    const gatt = device.gatt;
    if (!gatt) throw new Error('This Bluetooth device has no GATT server.');
    const server = gatt.connected ? gatt : await gatt.connect();
    this.assertCurrent(attempt);
    // The service check, which also picks the layout: classic (0x1820) if the device has it, else Microchip's UART.
    const found = await findDriverService(server, acaiaDriver);
    const layout = acaiaLayoutForService(found.uuid);
    if (!layout) throw new ScaleServiceMissingError(acaiaDriver);
    this.assertCurrent(attempt);
    const notifyChar = await found.service.getCharacteristic(layout.notifyChar);
    const writeChar = layout.writeChar === layout.notifyChar ? notifyChar : await found.service.getCharacteristic(layout.writeChar);
    this.assertCurrent(attempt);

    const cleanup: Array<() => void> = [];
    // A listener per attempt: a superseded attempt's cleanup must not remove a newer session's listener.
    const onValue = (ev: Event) => this.onNotification(ev, notifyChar);
    notifyChar.addEventListener('characteristicvaluechanged', onValue);
    cleanup.push(() => notifyChar.removeEventListener('characteristicvaluechanged', onValue));
    try {
      await notifyChar.startNotifications();
      this.assertCurrent(attempt);
    } catch (e) {
      cleanup.forEach((fn) => fn());
      throw e;
    }

    const now = performance.now();
    this.diag = { ...emptyDiagnostics(this.checksumMode), layout: layout.kind, writeMode: pickWriteMode(writeChar) };
    const session: Session = {
      server,
      layout,
      writeChar,
      timers: [],
      timeouts: [],
      cleanup,
      buffer: new AcaiaFrameBuffer(),
      lastWeightAt: now,
      resumeAt: now,
      lastCheckAt: now,
      lastHandshakeAt: now,
      stale: false,
      heartbeatPending: false,
      badStreak: 0,
      units: null,
      warning: null,
    };
    this.session = session;
    this.startWatchdog(session);
    session.timers.push(setInterval(() => this.heartbeat(session), HEARTBEAT_INTERVAL_MS));
    session.timeouts.push(setTimeout(() => this.handshake(session), HANDSHAKE_DELAY_MS));
  }

  private assertCurrent(attempt: number): void {
    if (attempt !== this.attempt) throw new Error('Connection attempt was cancelled.');
  }

  private endSession(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    s.timers.forEach((t) => clearInterval(t));
    s.timeouts.forEach((t) => clearTimeout(t));
    s.cleanup.forEach((fn) => fn());
    s.buffer.clear();
    if (s.warning) for (const cb of [...this.warningListeners]) safeCall(() => cb(null));
  }

  /** Only reported for established sessions: failures while connecting reject connect() instead. */
  private readonly onGattDisconnected = (): void => {
    if (!this.session) return;
    this.endSession();
    this.emitDisconnect('Lost connection to the scale.');
  };

  private emitDisconnect(reason?: string): void {
    for (const cb of [...this.disconnectListeners]) safeCall(() => cb(reason));
  }

  // ---------------------------------------------------------------- handshake and heartbeat

  /** Identify, then subscribe to events. Not awaited by anyone: a failure shows up as silence, and gets retried. */
  private handshake(s: Session): void {
    if (this.session !== s) return;
    s.lastHandshakeAt = performance.now();
    this.diag.handshakes++;
    void this.send('identify', acaiaCommands.identify(s.layout.kind)).catch(() => undefined);
    void this.send('notification request', acaiaCommands.notificationRequest()).catch(() => undefined);
  }

  private heartbeat(s: Session): void {
    if (this.session !== s || s.heartbeatPending) return;
    s.heartbeatPending = true;
    this.diag.heartbeatsSent++;
    // UART scales get identify before each heartbeat (pyacaia, Beanconqueror, aioacaia). A failed heartbeat is retried
    // by the next one; if the scale stops streaming, the watchdog takes it from there.
    if (s.layout.kind === 'uart') void this.send('identify', acaiaCommands.identify('uart')).catch(() => undefined);
    void this.send('heartbeat', acaiaCommands.heartbeat())
      .catch(() => undefined)
      .finally(() => {
        s.heartbeatPending = false;
      });
  }

  // ---------------------------------------------------------------- liveness watchdog

  private startWatchdog(s: Session): void {
    s.timers.push(setInterval(() => this.checkLiveness(s), WATCHDOG_CHECK_MS));
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    // A page that was hidden (and possibly frozen) restarts the silence clock instead of blaming the scale, and
    // wakes the stream up again: the heartbeat may have paused while it was hidden.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') return;
      const now = performance.now();
      s.resumeAt = now;
      s.lastCheckAt = now;
      this.handshake(s);
    };
    document.addEventListener('visibilitychange', onVisibility);
    s.cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  private checkLiveness(s: Session): void {
    if (this.session !== s) return;
    const now = performance.now();
    const sinceLastCheck = now - s.lastCheckAt;
    s.lastCheckAt = now;
    if (!pageVisible()) return;
    if (sinceLastCheck > WATCHDOG_CHECK_MS * 4) {
      // The timer itself was frozen or throttled (background tab, device sleep): our clock is unreliable.
      s.resumeAt = now;
      return;
    }
    const silentMs = now - Math.max(s.lastWeightAt, s.resumeAt);
    if (silentMs >= STALE_DISCONNECT_MS) {
      this.forceDisconnect('The scale stopped sending data.');
      return;
    }
    if (silentMs >= HANDSHAKE_RETRY_MS && now - s.lastHandshakeAt >= HANDSHAKE_RETRY_MS) this.handshake(s);
    if (!s.stale && silentMs >= STALE_MS) {
      s.stale = true;
      this.diag.stale = true;
      this.emitStale(true);
    }
  }

  private noteAlive(s: Session, t: number): void {
    s.lastWeightAt = t;
    if (s.stale) {
      s.stale = false;
      this.diag.stale = false;
      this.emitStale(false);
    }
  }

  private forceDisconnect(reason: string): void {
    if (!this.session) return;
    this.endSession();
    this.device.gatt?.disconnect();
    this.emitDisconnect(reason);
  }

  private emitStale(stale: boolean): void {
    for (const cb of [...this.staleListeners]) safeCall(() => cb(stale));
  }

  private setWarning(s: Session, warning: ScaleWarning | null): void {
    if (s.warning?.code === warning?.code) return;
    s.warning = warning;
    for (const cb of [...this.warningListeners]) safeCall(() => cb(warning));
  }

  // ---------------------------------------------------------------- incoming data

  private onNotification(ev: Event, ch: BluetoothRemoteGATTCharacteristic): void {
    // Bluefy dispatches plain objects rather than real Events; fall back to the characteristic itself.
    const value = (ev?.target as BluetoothRemoteGATTCharacteristic | null | undefined)?.value ?? ch.value;
    if (!value) return;
    const t = performance.now();
    // Copy: Chrome may reuse the underlying buffer, and raw listeners may keep the bytes.
    const bytes = new Uint8Array(value.byteLength);
    bytes.set(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    const d = this.diag;
    d.packets++;
    for (const cb of [...this.rawListeners]) safeCall(() => cb(bytes, t));
    const s = this.session;
    if (!s) return;

    const strict = this.checksumMode === 'strict';
    const frames = s.buffer.push(bytes, { dropBadChecksum: strict, onBadChecksum: () => this.checksumFailed(s) });
    d.junkBytes = s.buffer.stats.junkBytes;
    d.badLength = s.buffer.stats.badLength;
    for (const frame of frames) {
      if (this.session !== s) return;
      if (!frame.checksumOk) d.badChecksum++;
      else s.badStreak = 0;
      this.handleFrame(s, frame, t);
    }
  }

  private checksumFailed(s: Session): void {
    this.diag.badChecksum++;
    s.badStreak++;
    if (s.badStreak >= CHECKSUM_FAILURE_THRESHOLD && !s.warning) this.setWarning(s, { code: 'checksum', message: ACAIA_CHECKSUM_WARNING });
  }

  private handleFrame(s: Session, frame: AcaiaRawFrame, t: number): void {
    const d = this.diag;
    d.frames++;
    const msg = parseAcaiaFrame(frame);
    switch (msg.type) {
      case 'weight': {
        if (msg.weight.highBytes) d.highBytes++;
        // The link is alive either way: in ounces the scale streams, Taretto just doesn't use the weight.
        this.noteAlive(s, t);
        if (s.units === 'oz') {
          d.ouncesDropped++;
          return;
        }
        d.weightPackets++;
        if (s.warning?.code === 'checksum') this.setWarning(s, null);
        const reading: ScaleReading = { t, weightG: msg.weight.value, flowGs: null, timerMs: msg.timerMs, batteryPct: this.batteryPct };
        for (const cb of [...this.readingListeners]) safeCall(() => cb(reading));
        return;
      }
      case 'settings':
        d.settings++;
        d.units = msg.units;
        d.unitsByte = msg.unitsByte;
        d.batteryPct = msg.batteryPct;
        this.batteryPct = msg.batteryPct;
        s.units = msg.units;
        if (msg.units === 'oz') this.setWarning(s, { code: 'units', message: ACAIA_UNITS_WARNING });
        else if (s.warning?.code === 'units') this.setWarning(s, null);
        return;
      case 'button':
        d.buttons++;
        return;
      case 'malformed':
        d.malformed++;
        return;
      case 'other':
        d.other++;
        return;
      case 'timer':
      case 'ack':
      case 'info':
        return;
    }
  }

  // ---------------------------------------------------------------- commands

  /** Queue a command behind any in-flight write, at least MIN_WRITE_GAP_MS after the previous one. */
  private send(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    if (!this.connected) return Promise.reject(commandError(label, new Error('Scale not connected')));
    const run = () => this.writeSpaced(label, frame);
    const result = this.writeChain.then(run, run);
    this.writeChain = result.catch(() => undefined);
    return result;
  }

  private async writeSpaced(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const wait = this.lastWriteAt + MIN_WRITE_GAP_MS - performance.now();
    if (wait > 0) await delay(wait);
    if (!this.connected) throw commandError(label, new Error('Scale not connected'));
    try {
      await this.writeWithRetry(label, frame);
    } finally {
      this.lastWriteAt = performance.now();
    }
  }

  private async writeWithRetry(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    try {
      await this.writeAttempt(label, frame);
    } catch (first) {
      if (!this.connected) throw commandError(label, first);
      await delay(WRITE_RETRY_DELAY_MS);
      try {
        await this.writeAttempt(label, frame);
      } catch (second) {
        throw commandError(label, second);
      }
    }
  }

  /** One write, reported to the onRawWrite listeners whether it went out or not. */
  private async writeAttempt(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    let ok = false;
    try {
      await this.writeOnce(frame);
      ok = true;
    } finally {
      const t = performance.now();
      for (const cb of [...this.writeListeners]) safeCall(() => cb(frame, label, t, ok));
    }
  }

  private async writeOnce(frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const s = this.session;
    if (!s || !s.server.connected) throw new Error('Scale not connected');
    const ch = s.writeChar;
    // A fresh buffer the size of the frame: Bluefy has been reported to send a view's whole underlying buffer.
    const bytes = frame.slice();
    switch (pickWriteMode(ch)) {
      case 'withResponse':
        return ch.writeValueWithResponse(bytes);
      case 'withoutResponse':
        return ch.writeValueWithoutResponse(bytes);
      case 'legacy':
        return ch.writeValue(bytes);
    }
  }
}

// ---------------------------------------------------------------- helpers

function emptyDiagnostics(checksumMode: AcaiaChecksumMode): AcaiaDiagnostics {
  return {
    packets: 0,
    frames: 0,
    weightPackets: 0,
    badChecksum: 0,
    junkBytes: 0,
    badLength: 0,
    malformed: 0,
    highBytes: 0,
    ouncesDropped: 0,
    other: 0,
    buttons: 0,
    settings: 0,
    handshakes: 0,
    heartbeatsSent: 0,
    layout: null,
    units: null,
    unitsByte: null,
    batteryPct: null,
    checksumMode,
    writeMode: null,
    stale: false,
  };
}

/** The packet log's diagnostics line. */
function diagnosticsItems(d: AcaiaDiagnostics): ScaleDiagnosticsItem[] {
  const items: ScaleDiagnosticsItem[] = [
    { text: d.layout === 'uart' ? 'layout: Microchip UART' : d.layout === 'classic' ? 'layout: classic 0x1820/0x2A80' : 'layout: not connected' },
    { text: `${d.packets} notifications` },
    { text: `${d.frames} frames` },
    { text: `${d.weightPackets} weight` },
    { text: `${d.badChecksum} bad checksum${d.checksumMode === 'ignore' ? ' (still used)' : ' (dropped)'}`, tone: d.badChecksum > 0 ? 'warning' : undefined },
    { text: `${d.malformed} malformed (dropped)`, tone: d.malformed > 0 ? 'warning' : undefined },
    { text: `${d.other} other` },
    { text: `${d.handshakes} handshakes` },
    { text: `${d.heartbeatsSent} heartbeats` },
  ];
  if (d.junkBytes > 0) items.push({ text: `${d.junkBytes} bytes outside frames` });
  if (d.badLength > 0) items.push({ text: `${d.badLength} bad length bytes`, tone: 'warning' });
  if (d.highBytes > 0) items.push({ text: `${d.highBytes} weights with bytes 2–3 set (unverified)`, tone: 'warning' });
  if (d.settings === 0) items.push({ text: 'settings: not reported yet' });
  else if (d.units === 'oz') items.push({ text: `display in ounces: no weight (${d.ouncesDropped} dropped)`, tone: 'danger' });
  else if (d.units === 'unknown') items.push({ text: `units byte 0x${(d.unitsByte ?? 0).toString(16).padStart(2, '0')} not recognised, read as grams`, tone: 'warning' });
  if (d.batteryPct !== null) items.push({ text: `battery ${d.batteryPct} %` });
  if (d.buttons > 0) items.push({ text: `${d.buttons} button presses` });
  if (d.checksumMode === 'ignore') items.push({ text: 'checksum: ignored', tone: 'warning' });
  if (d.writeMode) items.push({ text: `writes: ${d.writeMode}` });
  if (d.stale) items.push({ text: 'no data (stale)', tone: 'danger' });
  return items;
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function commandError(label: string, cause: unknown): Error {
  const msg = cause instanceof Error ? cause.message : String(cause);
  return new Error(`Couldn't send "${label}" to the scale: ${msg}`, { cause });
}

function subscribe<T>(set: Set<T>, cb: T): () => void {
  set.add(cb);
  return () => {
    set.delete(cb);
  };
}

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    console.error('Acaia scale listener failed', e);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
